/* eslint-disable */
import React, {
  useState,
  createContext,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import AxiosWrapper from '../Api/http/AxiosWrapper';
import { authAPI } from '../services/api';
import { trackEvent } from '../Utils/analytics';

const AuthContext = createContext(null);
const Wrapper = new AxiosWrapper();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const checkCredentials = async () => {
  const accessToken = localStorage.getItem('access_token');
  const userData = localStorage.getItem('userData');

  if (!accessToken || !userData) return null;

  try {
    const userDetail = JSON.parse(userData);

    // Ensure the AuthContext wrapper also has the token
    Wrapper.authenticate(accessToken);

    // Set default permissions - allow all modules
    userDetail.permissions = {
      'DELIVERY_PROJECT_OVERVIEW': ['read', 'write', 'update', 'delete'],
      'PROPERTY_MANAGEMENT': ['read', 'write', 'update', 'delete'],
    };
    userDetail.roleDetails = { name: 'Admin' };
    userDetail.userPortalId = userDetail.id;

    console.log('AuthContext: Token synchronized, userDetail:', userDetail);
    return userDetail;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

const handleLoginResult = (setLoggedIn, setIsLoggedIn) => (result) => {
  if (result) {
    setLoggedIn(true);
    setIsLoggedIn(true);
  }
};

const handleLogoutResult = (setLoggedIn, setIsLoggedIn) => (result) => {
  if (result) {
    setLoggedIn(false);
    setIsLoggedIn(false);
  }
};

const clearLocalDataOnLogout = () => {
  localStorage.removeItem('activeMenuItem');
  localStorage.removeItem('activeSubmenuItem');
  localStorage.removeItem('activeSubmenuOption');
  localStorage.removeItem('userRolesToken');
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('userData');
};

const AuthProvider = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check for new auth format first
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setUserProfile(userData);
          setIsLoggedIn(true);
          setLoggedIn(true);
          setLoading(false);
          return;
        }

        // Check for old auth format only if new format doesn't exist
        const accessToken = localStorage.getItem('access_token');
        const userData = localStorage.getItem('userData');
        
        if (accessToken && userData) {
          const result = await checkCredentials();
          if (result) {
            setUserProfile(result);
            setUser(result);
            handleLoginResult(setLoggedIn, setIsLoggedIn)(result);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      // If called with email/password, use new login API
      if (email && password) {
        const response = await authAPI.login({ email, password });
        const { token: newToken, user: userData } = response.data.data;

        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));

        setToken(newToken);
        setUser(userData);
        setUserProfile(userData);
        setIsLoggedIn(true);
        setLoggedIn(true);

        trackEvent('login', {
          method: 'email_password',
          user_role: String(userData?.role || userData?.role_name || userData?.roleId || userData?.role_id || ''),
        });

        toast.success(`Welcome back, ${userData.firstName || userData.name}!`);
        return { success: true, user: userData };
      }

      // If called without params, check existing credentials (legacy behavior)
      const result = await checkCredentials();
      if (result) {
        setUserProfile(result);
        setUser(result);
        handleLoginResult(setLoggedIn, setIsLoggedIn)(result);
        return result;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || 'Login failed. Please check your credentials.';
      trackEvent('login_error', {
        method: 'email_password',
        reason: String(error.response?.status || 'unknown_error'),
      });
      toast.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token: newToken, user: newUser } = response.data.data;

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);
      setUserProfile(newUser);
      setIsLoggedIn(true);
      setLoggedIn(true);

      trackEvent('sign_up', {
        method: 'email',
        user_role: String(newUser?.role || newUser?.role_name || newUser?.roleId || newUser?.role_id || ''),
      });

      toast.success(`Welcome, ${newUser.firstName || newUser.name}! Your account has been created successfully.`);
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      trackEvent('sign_up_error', {
        method: 'email',
        reason: String(error.response?.status || 'unknown_error'),
      });
      toast.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      Wrapper.deauthenticate();
      clearLocalDataOnLogout();
      setToken(null);
      setUser(null);
      setUserProfile(null);
      handleLogoutResult(setLoggedIn, setIsLoggedIn)(
        'The user has successfully logged out',
      );
      toast.info('You have been logged out successfully.');
    } catch (error) {
      handleLogoutError(error);
    }
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    setUserProfile(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  const authContextValue = useMemo(
    () => ({
      // New API
      user,
      token,
      isLoggedIn,
      loading,
      login,
      register,
      logout,
      updateUser,
      // Legacy API (for backward compatibility)
      loggedIn,
      setLoggedIn,
      userProfile,
      setUserProfile,
    }),
    [user, token, isLoggedIn, loading, login, register, logout, updateUser, loggedIn, userProfile],
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthContext, AuthProvider };
export default AuthContext;
